import areaConfig from '../data/releaseAreaRules.json';

export interface ReleaseAreaOption {
  area: string;
  icon: string;
}

export const DEFAULT_RELEASE_AREA: ReleaseAreaOption = areaConfig.defaultArea;

/** CRM areas available in the release-notes admin picker (default + all path rules). */
export const RELEASE_AREA_OPTIONS: ReleaseAreaOption[] = [
  DEFAULT_RELEASE_AREA,
  ...areaConfig.rules.map((rule) => ({ area: rule.area, icon: rule.icon })),
];

export function iconForReleaseArea(area: string | undefined): string {
  if (!area) return DEFAULT_RELEASE_AREA.icon;
  const match = RELEASE_AREA_OPTIONS.find((option) => option.area === area);
  return match?.icon ?? DEFAULT_RELEASE_AREA.icon;
}

/** True when the title adds nothing beyond the area chip + bullets. */
export function isGenericReleaseTitle(title: string, area?: string): boolean {
  const trimmed = (title || '').trim();
  if (!trimmed) return true;
  if (area && trimmed === area) return true;
  if (area && trimmed === `${area} updates`) return true;
  if (area && new RegExp(`^${escapeRegExp(area)}\\s—\\s\\d+\\s+updates$`).test(trimmed)) {
    return true;
  }
  return false;
}

const WHATS_NEW_IN_RE = /^what's new in (.+)$/i;

/** Functions-only / default CRM — never headline staff notes as "Backend". */
export function defaultWhatsNewTitle(area?: string): string {
  if (!area || area === 'CRM' || area === 'Backend') return "What's new in the CRM";
  return `What's new in ${area}`;
}

export function isAutoWhatsNewTitle(title: string): boolean {
  return WHATS_NEW_IN_RE.test((title || '').trim());
}

export function whatsNewAreaFromTitle(title: string): string | null {
  const match = (title || '').trim().match(WHATS_NEW_IN_RE);
  return match?.[1] ?? null;
}

/** Follow a generic "What's new in X" headline when the area chip changes. */
export function followWhatsNewTitle(currentTitle: string, previousArea?: string, nextArea?: string): string {
  if (!isAutoWhatsNewTitle(currentTitle)) return currentTitle;
  const titled = whatsNewAreaFromTitle(currentTitle);
  const previousLabels = [previousArea, 'Backend', 'the CRM', 'CRM'].filter(Boolean);
  if (titled && !previousLabels.some((label) => label!.toLowerCase() === titled.toLowerCase())) {
    return currentTitle;
  }
  return defaultWhatsNewTitle(nextArea);
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}
